import PropTypes from 'prop-types';
import { forwardRef, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
// @mui
import {
  Dialog,
  Slide,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Stack,
  Typography,
  Chip,
  Skeleton,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// swr
import { mutate } from "swr"
// hooks
import useAuth from '../hooks/useAuth';
import useResponsive from '../hooks/useResponsive';
// utils
import axios from '../utils/axios';

// ----------------------------------------------------------------------

const Transition = forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

EmptyProfileGuard.propTypes = {
  children: PropTypes.node,
};

export default function EmptyProfileGuard({ children, profileMutate=false }) {
  const { user, application, getUser } = useAuth();
  const [openMainPositionDialog, setOpenMainPositionDialog] = useState(false);
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [hasNext, setHasNext] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [groupRole, setGroupRole] = useState([]);
  const [selectedChip, setSelectedChip] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState([]);
  const smUp = useResponsive('up', 'sm');

  const loadCategory = async () => {
    const categoryResponse = await axios.get('api/common/categories/');
    setCategoryOptions(categoryResponse.data);
  }

  const selectMainRole = async (role) => {
    setSelectedChip(role);
  }

  const selectCategory = async (category) => {
    const newCategory = selectedCategory
    const index = newCategory.findIndex(c => c.name === category.name);
    if (index > -1)  newCategory.splice(index, 1);
    else if (newCategory.length < 3) newCategory.push(category);
    setSelectedCategory([...newCategory]);
  }

  const submitPosition = async () => {
    if (!selectedChip) {
      enqueueSnackbar('포지션을 선택해주세요', { variant: 'error' });
      return;
    }
    try {
      setIsLoading(true);
      await axios.patch(
        'api/crews/select-main-position/',
        selectedChip,
      );
      enqueueSnackbar('저장되었습니다');
      if (hasNext) {
        setOpenMainPositionDialog(false);
        setOpenCategoryDialog(true);
      } else {
        getUser();
        if (profileMutate) mutate(`/api/crews/application/`);
      }
      setIsLoading(false);
    } catch(err) {
      setIsLoading(false);
      console.error(err);
      enqueueSnackbar('포지션 저장에 실패하였습니다. 관리자에 문의해주세요', { variant: 'error' });
    }
  }

  const submitCategory = async () => {
    if (!selectedCategory.length) {
      enqueueSnackbar('카테고리를 선택해주세요', { variant: 'error' });
      return;
    }
    try {
      setIsLoading(true);
      await axios.patch(
        'api/crews/select-category/',
        selectedCategory,
      );
      enqueueSnackbar('저장되었습니다');
      getUser();
      if (profileMutate) mutate(`/api/crews/application/`);
      setIsLoading(false);
    } catch(err) {
      setIsLoading(false);
      console.error(err);
      enqueueSnackbar('카테고리 저장에 실패하였습니다. 관리자에 문의해주세요', { variant: 'error' });
    }
  }

  const getGroupBaseList = async () => {
    if (application?.matching_roles.length > 0) {
      return application?.matching_roles;
    } else {
      const rolesResponse = await axios.get('api/crews/career/matching-roles/');
      return rolesResponse.data;
    }
  }

  const generateGroupRole = async () => {
    const groupBaseList = await getGroupBaseList();
    const groupedRoles = groupBaseList.reduce((result, role) => {
      const { group } = role;
      result[group] = result[group] ?? [];
      result[group].push(role);
      return result;
    }, {});
    setGroupRole(groupedRoles);
  }

  const closePositionDialog = async () => {
    setOpenMainPositionDialog(false);
    setOpenCategoryDialog(false);
  }

  useEffect(() => {
    if (user?.crew) {
      if (!user.crew.main_role) {
        setOpenMainPositionDialog(!user.crew.main_role);
        generateGroupRole();
        if (!user.crew.categories?.length) {
          setHasNext(true);
          loadCategory();
        }
      } else if (!user.crew.categories?.length) {
        setOpenCategoryDialog(!user.crew.categories?.length);
        loadCategory();
      } else {
        setOpenMainPositionDialog(false);
        setOpenCategoryDialog(false);
      }
    }
  }, [user]);

  return (
    <>
      {children}
      <Dialog maxWidth="sm" fullWidth open={openMainPositionDialog} TransitionComponent={Transition}>
        <DialogTitle paragraph>
          가장 잘할 수 있는 포지션을 선택해주세요.
        </DialogTitle>
        <DialogContent>
          <DialogContentText paragraph>
            딱 맞는 회사와 매칭받기 위해 선택하신 포지션 중 가장 잘할 수 있는 포지션을 <Typography display={"inline"} sx={{fontWeight: 'bold'}}>한 개만</Typography> 선택해 주세요.
          </DialogContentText>
          {Object.keys(groupRole).map((g, gi) => (
            (user?.crew?.position === 'developer') ? (
            <Stack direction={smUp ? "row" : "column"} key={gi} alignItems="baseline">
              <Typography
                variant="body2"
                sx={{
                  pt: 1.6,
                  pr: 2,
                  pl: 0,
                  width: smUp ? '25%' : '100%',
                  textAlign: smUp ? 'right' : 'left',
                  fontWeight: 500
                }}>
                {g}
              </Typography>
              <Stack sx={{ width: smUp ? '75%' : '100%' }} direction="row" flexWrap="wrap" display="flex" gap={1}>
                {groupRole[g].map((r, ri) => (
                  <Chip
                    size="medium"
                    color={selectedChip?.role === r.role ? "primary" : "default"}
                    variant={selectedChip?.role === r.role ? "filled" : "outlined"}
                    label={r.role}
                    key={`${gi}-${ri}`}
                    onClick={() => selectMainRole(r)}
                  />
                ))}
              </Stack>
            </Stack>
            ) : (
              <>
                {groupRole[g].map((r, ri) => (
                  <Chip
                    sx={{ mr: 1, mb: 1 }}
                    size="medium"
                    color={selectedChip?.role === r.role ? "primary" : "default"}
                    variant={selectedChip?.role === r.role ? "filled" : "outlined"}
                    label={r.role}
                    key={`${gi}-${ri}`}
                    onClick={() => selectMainRole(r)}
                  />
                ))}
              </>
            )
          ))}
        </DialogContent>
        <DialogActions>
          <LoadingButton
            onClick={closePositionDialog}
            loading={isLoading}
            sx={{ px: {xs: 5, sm: 7}, whiteSpace: 'nowrap' }}
          >
            취소
          </LoadingButton>
          <LoadingButton
            variant="contained"
            disabled={!selectedChip}
            onClick={submitPosition}
            loading={isLoading}
            sx={{ px: {xs: 5, sm: 7}, whiteSpace: 'nowrap' }}
          >
            {hasNext ? "다음" : "제출하기"}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog maxWidth="xs" fullWidth open={openCategoryDialog} TransitionComponent={Transition}>
        <DialogTitle paragraph>
          어떤 분야에서 일해보셨는지 알려주세요.
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            딱 맞는 회사와 매칭해 드리기 위해 경력 카테고리가 추가되었습니다.
          </DialogContentText>
          <DialogContentText sx={{ mb: 3 }}>
            일해보신 자신 있는 분야를 <Typography display={"inline"} sx={{fontWeight: 'bold'}}>최대 3개</Typography>까지 선택해 주세요.
          </DialogContentText>
          {Object.keys(categoryOptions).length === 0 && (
            <Skeleton variant="rectangular" height={180} />
          )}
          {Object.keys(categoryOptions).length > 0 && (
          <Stack direction="row" flexWrap="wrap" display="flex" gap={1}>
            {categoryOptions?.map((c, ci) => (
              <Chip
                size="medium"
                color={selectedCategory?.find(sc => sc.name === c.name) ? "primary" : "default"}
                variant={selectedCategory?.find(sc => sc.name === c.name) ? "filled" : "outlined"}
                label={c.name}
                key={ci}
                onClick={() => selectCategory(c)}
              />
            ))}
          </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <LoadingButton
            onClick={closePositionDialog}
            loading={isLoading}
            sx={{ px: {xs: 5, sm: 7}, whiteSpace: 'nowrap' }}
          >
            취소
          </LoadingButton>
          <LoadingButton
            variant="contained"
            onClick={submitCategory}
            disabled={!selectedCategory.length}
            loading={isLoading}
            sx={{ px: {xs: 5, sm: 7}, whiteSpace: 'nowrap' }}
          >
            제출하기
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
